import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Search, User, ShoppingBag, Heart, Menu,
  ChevronRight, ChevronDown, Layers, Sparkles,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { useSiteConfig } from '../context/SiteConfigContext';
import { useCart } from '../context/CartContext';
import { CategoryContext } from '../context/CategoryContext';
import Auth from '../utils/AuthUtils';

import CartCanvas from '../pages/canvas/CartCanvas';
import SearchCanvas from '../pages/canvas/SearchCanvas';

/* ─────────────────────────────────────────────
   RECURSIVE MOBILE MENU ITEM
   Handles unlimited nesting levels
───────────────────────────────────────────── */
function MobileMenuItem({ cat, depth = 0 }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = cat.children?.length > 0;

  const paddingLeft = 18 + depth * 14;

  return (
    <li className="nav-mb-item">
      <div
        className="mb-menu-link d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer', paddingLeft }}
        onClick={() => hasChildren && setIsOpen((p) => !p)}
      >
        {hasChildren ? (
          <span style={{ fontSize: depth === 0 ? '14px' : depth === 1 ? '13px' : '12px', fontWeight: depth === 0 ? 500 : 400, color: depth === 0 ? 'var(--luxury-royal)' : 'var(--luxury-slate)' }}>
            {cat.name}
          </span>
        ) : (
          <Link
            to={`/categories/${cat.slugPath}`}
            className="text-decoration-none w-100"
            data-bs-dismiss="offcanvas"
            style={{ fontSize: depth === 0 ? '14px' : depth === 1 ? '13px' : '12px', fontWeight: depth === 0 ? 500 : 400, color: depth === 0 ? 'var(--luxury-royal)' : 'var(--luxury-slate)' }}
          >
            {cat.name}
          </Link>
        )}

        {hasChildren && (
          <span className="plus-minus-icon" style={{ fontSize: '16px', color: 'var(--luxury-slate)', transition: 'transform 0.2s', transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}>
            +
          </span>
        )}
      </div>

      {hasChildren && isOpen && (
        <div style={{ background: depth === 0 ? 'rgba(91,149,198,0.04)' : depth === 1 ? 'rgba(91,149,198,0.07)' : 'rgba(91,149,198,0.1)', borderLeft: '2px solid rgba(212,175,55,0.25)', marginLeft: paddingLeft }}>
          <div style={{ paddingLeft: 14 }}>
            <Link
              to={`/categories/${cat.slugPath}`}
              data-bs-dismiss="offcanvas"
              className="text-decoration-none"
              style={{ display: 'block', padding: '7px 0', fontSize: '11px', fontWeight: 600, color: 'var(--luxury-gold)', letterSpacing: '0.3px' }}
            >
              View all {cat.name} →
            </Link>
          </div>

          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {cat.children.map((child) => (
              <MobileMenuItem key={child._id} cat={child} depth={depth + 1} />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

/* ─────────────────────────────────────────────
   FLYOUT MEGA MENU — 4-level panel component
───────────────────────────────────────────── */
function FlyoutMegaMenu({ categories, onClose }) {
  const [activePath, setActivePath] = useState([]); // active category id har depth pe

  // columns dynamically build karo activePath ke hisaab se
  const columns = [];
  let levelItems = categories || [];
  let depth = 0;

  while (levelItems && levelItems.length > 0) {
    columns.push(levelItems);
    const activeId = activePath[depth];
    const activeItem = levelItems.find((c) => c._id === activeId);
    if (!activeItem) break; // is level pe kuch select nahi hua, toh aage mat badho
    levelItems = activeItem.children || [];
    depth++;
  }

  const handleHover = (depthIndex, id) => {
    setActivePath((prev) => {
      const next = prev.slice(0, depthIndex); // is depth ke baad ka purana selection clear karo
      next[depthIndex] = id;
      return next;
    });
  };

  return (
    <div className="flyout-mega-root">
      {columns.map((items, colIndex) => {
        const parentId = colIndex > 0 ? activePath[colIndex - 1] : null;
        const parent =
          colIndex > 0
            ? columns[colIndex - 1].find((c) => c._id === parentId)
            : null;

        return (
          <div key={colIndex} className="flyout-col">
            {colIndex > 0 && parent && (
              <div className="flyout-col__heading">
                <Link
                  to={`/categories/${parent.slugPath}`}
                  className="flyout-col__heading-link"
                  onClick={onClose}
                >
                  {parent.name}
                </Link>
              </div>
            )}

            {items.map((cat) => {
              const hasChildren = cat.children?.length > 0;
              const isActive = activePath[colIndex] === cat._id;

              return hasChildren ? (
                <button
                  key={cat._id}
                  className={`flyout-item ${isActive ? 'is-active' : ''}`}
                  onMouseEnter={() => handleHover(colIndex, cat._id)}
                  onClick={() => handleHover(colIndex, cat._id)}
                >
                  <span className="flyout-item__label text-decoration-none">{cat.name}</span>
                  <ChevronRight size={13} className="flyout-item__arrow" />
                </button>
              ) : (
                <Link
                  key={cat._id}
                  to={`/categories/${cat.slugPath}`}
                  className={`flyout-item text-decoration-none flyout-item--leaf ${isActive ? 'is-active' : ''}`}
                  onClick={onClose}
                >
                  <span className="flyout-item__label">{cat.name}</span>
                </Link>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN HEADER COMPONENT
───────────────────────────────────────────── */
export default function LuxuryTokooHeader() {
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);

  const navigate = useNavigate();

  const menuRef = useRef(null);
  const { config } = useSiteConfig();
  const { cartItems } = useCart();
  const { categories } = useContext(CategoryContext);
  const isLoggedIn = Auth.getToken();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMegaMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mobile offcanvas ke andar Login/Dashboard link ke liye —
  // offcanvas ko manually close karke clean navigate karta hai,
  // taaki touch devices pe dismiss + Link click ka race condition na ho
  const handleMobileNavLink = (path) => (e) => {
    e.preventDefault();
    const offcanvasEl = document.getElementById('mobileMenu');
    const instance = window.bootstrap?.Offcanvas?.getInstance(offcanvasEl);
    if (instance) {
      instance.hide();
    }
    navigate(path);
  };

  if (!config) return null;

  return (
    <>
      {/* ── Off-canvas: Cart only (login/register are now pages) ── */}
      <div className="offcanvas offcanvas-end custom-cart-offcanvas" id="shoppingCartCanvas">
        <CartCanvas />
      </div>

      {/* ── Mobile off-canvas menu ── */}
      <div className="offcanvas offcanvas-start canvas-mb" id="mobileMenu">
        <button className="icon-close icon-close-popup" data-bs-dismiss="offcanvas" aria-label="Close" />
        <div className="mb-canvas-content">
          <div className="mb-body">
            <div className="mb-content-top">
              <ul className="nav-ul-mb" id="wrapper-menu-navigation">

                <li className="nav-mb-item">
                  <Link to="/"  className="mb-menu-link" >Home</Link>
                </li>

                {categories?.map((cat) => (
                  <MobileMenuItem key={cat._id} cat={cat} depth={0} />
                ))}

                <li className="nav-mb-item">
                  <Link to="/contact-us" className="mb-menu-link" >Contact</Link>
                </li>

              </ul>
               <div className="mobile-bulk-enquiry-bar d-flex d-lg-none">
          <Link to="/bulk-enquiry" className="bulk-enquiry-btn">
            <Sparkles size={13} />
            Bulk Enquiry
          </Link>
        </div>
            </div>

            <div className="mb-other-content">
              <div className="group-icon">

                {!isLoggedIn ? (
                  <Link
                    to="/users/login"
                    className="shop-btn"
                    onClick={handleMobileNavLink('/users/login')}
                  >
                    <User size={16} className="me-2" /> Login
                  </Link>
                ) : (
                  <Link
                    to="/users/dashboard"
                    className="shop-btn"
                    onClick={handleMobileNavLink('/users/dashboard')}
                  >
                    <User size={16} className="me-2" /> Dashboard
                  </Link>
                )}
              </div>
              {config?.address && (
                <div className="mb-contact mb-0 border-bottom border-muted">
                  <p>
                    Address:
                    <span className="ms-1">
                      {[config.address.addressLine, config.address.district, config.address.state].filter(Boolean).join(', ')}
                      {config.address.pincode ? ` - ${config.address.pincode}` : ''}
                    </span>
                  </p>
                </div>
              )}
              <ul className="pt-3">
                {config?.email && (
                  <li>Email: <b className="fw-medium"><a className="text-decoration-none text-secondary" href={`mailto:${config.email}`}>{config.email}</a></b></li>
                )}
                {config?.phone && (
                  <li>Phone: <b className="fw-medium"><a className="text-decoration-none text-secondary" href={`tel:${config.phone}`}>+91-{config.phone}</a></b></li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ════════ MAIN HEADER ════════ */}
      <header className="premium-header" ref={menuRef} style={{ position: 'relative', zIndex: 1050 }}>
        <div className="container-fluid px-3 px-lg-5 py-3">
          <div className="row align-items-center g-3">

            {/* Logo */}
            <div className="col-6 col-lg-4 d-flex align-items-center gap-2">
              <button
                className="btn d-lg-none p-0 me-2"
                data-bs-toggle="offcanvas"
                data-bs-target="#mobileMenu"
                aria-label="Toggle Navigation"
                style={{ color: 'var(--luxury-royal)', display: 'flex', alignItems: 'center' }}
              >
                <Menu size={24} />
              </button>
              <Link to="/" className="custom-icon-text-dec">
                {config?.logo ? (
                  <img src={config.logo} alt={config?.storeName || 'Logo'} style={{ height: '62px', objectFit: 'contain' }} />
                ) : (
                  <h2 className="mb-0 d-flex align-items-center gap-2 logo-text" style={{ fontSize: '1.5rem' }}>
                    <div style={{ background: 'var(--luxury-royal)', padding: '7px', borderRadius: '10px', display: 'flex' }}>
                      <Layers size={20} color="white" />
                    </div>
                    <span>{config?.storeName || 'Vigital'}</span>
                  </h2>
                )}
              </Link>
            </div>

            {/* Desktop Search */}
            <div className="col-lg-4 d-none d-lg-block">
              <div className="search-container-premium" style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search products, brands and collections..."
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setShowSearchModal(true); }}
                />
                <button className="btn px-3 py-1" style={{ color: 'var(--luxury-gold)', display: 'flex', alignItems: 'center' }}>
                  <Search size={18} />
                </button>
                <SearchCanvas isOpen={showSearchModal} searchText={searchText} setSearchText={setSearchText} />
              </div>
            </div>

            {/* Right Actions */}
            <div className="col-6 col-lg-4 d-flex justify-content-end align-items-center gap-3">

              <button
                className="btn d-lg-none p-1"
                style={{ color: 'var(--luxury-royal)', display: 'flex', alignItems: 'center' }}
                onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
                aria-label="Toggle Search"
              >
                <Search size={22} />
              </button>

              <Link to="/wishlist" className="d-flex align-items-center gap-1 top-nav-link text-decoration-none" style={{ color: '#2174AC' }}>
                <Heart size={20} />
              </Link>

              {isLoggedIn ? (
                <Link to="/users/dashboard" className="custom-login-dec text-decoration-none d-none d-lg-flex">
                  <div className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill" style={{ background: 'var(--luxury-royal-light)', cursor: 'pointer' }}>
                    <User size={16} style={{ color: 'var(--luxury-royal)' }} />
                    <span className="small" style={{ fontSize: '13px', fontWeight: '500', color: 'var(--luxury-royal)' }}>Dashboard</span>
                  </div>
                </Link>
              ) : (
                <Link to="/users/login" className="custom-login-dec text-decoration-none d-none d-lg-flex">
                  <div className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill" style={{ background: 'var(--luxury-royal-light)', cursor: 'pointer' }}>
                    <User size={16} style={{ color: 'var(--luxury-royal)' }} />
                    <span className="small" style={{ fontSize: '13px', fontWeight: '500', color: 'var(--luxury-royal)' }}>Sign In</span>
                  </div>
                </Link>
              )}

              <button
                className="cart-trigger border-0 bg-transparent position-relative p-1"
                data-bs-toggle="offcanvas"
                data-bs-target="#shoppingCartCanvas"
                style={{ cursor: 'pointer', color: 'var(--luxury-royal)', display: 'flex', alignItems: 'center' }}
                aria-label="Open Cart"
              >
                <ShoppingBag size={22} />
                {cartItems.length > 0 && (
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-circle"
                    style={{ fontSize: '9px', background: 'var(--luxury-gold)', color: '#fff', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {cartItems.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Search Expand */}
          {mobileSearchOpen && (
            <div className="row mt-3 d-lg-none">
              <div className="col-12" style={{ position: 'relative' }}>
                <div className="search-container-premium">
                  <input
                    type="text"
                    className="w-100"
                    placeholder="Search products..."
                    autoFocus
                    value={searchText}
                    onChange={(e) => { setSearchText(e.target.value); setShowSearchModal(true); }}
                  />
                  <button className="btn px-3" style={{ color: 'var(--luxury-royal)', display: 'flex', alignItems: 'center' }}>
                    <Search size={18} />
                  </button>
                </div>
                <SearchCanvas isOpen={showSearchModal} searchText={searchText} setSearchText={setSearchText} />
              </div>
            </div>
          )}
        </div>

        {/* Mobile Bulk Enquiry Bar — only on small screens */}
       

        {/* Category Bar (Desktop) */}
        <div className="category-bar-premium d-none d-lg-flex">
          <div className="container-fluid px-lg-5 d-flex align-items-center h-100">
            <button
              className="nav-trigger h-100"
              onClick={() => setIsMegaMenuOpen((prev) => !prev)}
              aria-expanded={isMegaMenuOpen}
              style={{color: "#2174ac"}}
            >
              <Menu size={16} style={{color:"#2174ac"}} /> ALL CATEGORIES {isMegaMenuOpen ? '▴' : '▾'}
            </button>
            <nav className="d-flex align-items-center gap-4 ms-4">
              <Link to="/shop" className="top-nav-link text-uppercase text-decoration-none" style={{ fontSize: '12px', letterSpacing: '0.8px', color: 'rgba(255,255,255,0.9)', fontWeight: '600' }}>
                Shop
              </Link>
              {categories?.slice(0, 4).map((cat) => (
                <Link
                  key={cat._id}
                  to={`/categories/${cat.slugPath}`}
                  className="top-nav-link text-uppercase text-decoration-none"
                  style={{ fontSize: '12px', letterSpacing: '0.8px', color: 'rgba(255,255,255,0.9)', fontWeight: '600' }}
                >
                  {cat.name}
                </Link>
              ))}
            </nav>

            {/* Bulk Enquiry — glowing CTA, pushed to the right corner */}
            <Link to="/bulk-enquiry" className="bulk-enquiry-btn ms-auto">
              <Sparkles size={14} />
              Bulk Enquiry
            </Link>
          </div>
        </div>

        {/* 4-Level Flyout Mega Menu */}
        {isMegaMenuOpen && (
          <div className="flyout-mega-wrapper">
            <FlyoutMegaMenu categories={categories} onClose={() => setIsMegaMenuOpen(false)} />
          </div>
        )}
      </header>
    </>
  );
}